import React, { useState, useEffect } from 'react';
import {
  Box, Select, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Snackbar, Alert, TextField, Button, CircularProgress, Backdrop, SelectChangeEvent
} from '@mui/material';
import { Add, CheckCircle, Cancel, Download } from '@mui/icons-material';
import axios from 'axios';

// import BankReport from './reports_doc/BankReport';
// import CashReport from './reports_doc/CashReport';
import { generateMathReport } from './reports_doc/MathReport';

interface MonthOption {
  value: string;
  label: string;
}

interface ReportData {
  id: number;
  pharmacyNum: string;
  balanceOfStart: number;
  [key: string]: any;
}

interface ColumnConfig {
  field: string;
  headerName: string;
  editable: boolean;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

interface EditingCell {
  rowId: number;
  field: string;
  value: string | number;
  initialValue: string | number;
}

const CurrentMathReport: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonth());
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [columns, setColumns] = useState<ColumnConfig[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [isRecalculating, setIsRecalculating] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    document.title = 'Текущий мат.отчет | Zerde';
    fetchColumns();
  }, []);

  useEffect(() => {
    if (columns.length > 0) {
      fetchReportData();
    }
  }, [selectedMonth, columns]);

  const fetchColumns = async () => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/mathreport-fields');
      if (response.data) {
        const formattedColumns: ColumnConfig[] = response.data.map((field: any) => ({
          field: field.field,
          headerName: field.headerName,
          editable: field.editable
        }));
        setColumns(formattedColumns);
      }
    } catch (error) {
      console.error('Ошибка при получении конфигурации полей:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при получении конфигурации полей: ' + error,
        severity: 'error',
      });
    }
  };

  function getCurrentMonth(): string {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }

  function getMonthsList(): MonthOption[] {
    const months = [];
    const currentDate = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleString('ru', { year: 'numeric', month: 'long' });
      months.push({ value, label });
    }
    return months;
  }

  const fetchReportData = async () => {
    if (isRecalculating) return; // Prevent data fetching during recalculation
    
    setLoading(true);
    try {
      const [year, month] = selectedMonth.split('-');

      const response = await axios.get('https://tbl.zerdegroup.kz/api/mathdata-check', {
        params: {
          month: Number(month),
          year: Number(year)
        }
      });

      if (response.data) {
        setReportData(response.data);
        if (response.data.length > 0) {
          setSnackbar({
            open: true,
            message: `Найдено записей: ${response.data.length}`,
            severity: 'success',
          });
        }
      }
    } catch (error) {
      console.error('Ошибка при получении данных:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при получении данных: ' + error,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = (rowId: number, field: string, value: any) => {
    if (isRecalculating) return; // Prevent cell editing during recalculation
    
    const column = columns.find(col => col.field === field);
    if (!column?.editable) return;

    if (field === 'checkStatus') {
      handleCheckStatusChange(rowId, value);
      return;
    }

    const safeValue = value ?? '';
    const displayValue = typeof safeValue === 'number'
      ? safeValue.toString().replace('.', ',')
      : safeValue;

    setEditingCell({
      rowId,
      field,
      value: displayValue,
      initialValue: safeValue
    });
  };

  const handleCheckStatusChange = async (rowId: number, currentValue: number) => {
    if (isRecalculating) return; // Prevent status changes during recalculation
    
    const newValue = currentValue === 1 ? 0 : 1;

    try {
      await axios.post('https://tbl.zerdegroup.kz/api/mathdata-update-status', {
        id: rowId,
        value: newValue
      });

      setReportData(prevData =>
        prevData.map(row =>
          row.id === rowId
            ? { ...row, checkStatus: newValue }
            : row
        )
      );

      setSnackbar({
        open: true,
        message: `Статус успешно ${newValue === 1 ? 'установлен' : 'снят'}`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Ошибка при обновлении статуса:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при обновлении статуса: ' + error,
        severity: 'error',
      });
    }
  };

  const handleCellChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!editingCell || isRecalculating) return; // Prevent cell editing during recalculation
    
    let inputValue = event.target.value;
    if (!/^-?\d*[.,]?\d*$/.test(inputValue)) {
      return;
    }

    setEditingCell({ ...editingCell, value: inputValue });
  };

  const handleCellBlur = async () => {
    if (!editingCell || isRecalculating) {
      setEditingCell(null);
      return;
    }

    let processedValue = editingCell.value.toString()
      .replace(/\s/g, '')
      .replace(',', '.');

    if (processedValue === '' || processedValue === editingCell.initialValue.toString()) {
      setEditingCell(null);
      return;
    }

    const numericValue = Number(processedValue);

    if (isNaN(numericValue)) {
      setSnackbar({
        open: true,
        message: 'Пожалуйста, введите корректное число',
        severity: 'error',
      });
      return;
    }

    try {
      await axios.post('https://tbl.zerdegroup.kz/api/mathdata-update', {
        id: editingCell.rowId,
        field: editingCell.field,
        value: numericValue
      });

      setReportData(prevData =>
        prevData.map(row =>
          row.id === editingCell.rowId
            ? { ...row, [editingCell.field]: numericValue }
            : row
        )
      );

      setSnackbar({
        open: true,
        message: 'Значение успешно обновлено',
        severity: 'success',
      });
    } catch (error) {
      console.error('Ошибка при обновлении:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при обновлении значения: ' + error,
        severity: 'error',
      });
    } finally {
      setEditingCell(null);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    if (isRecalculating) return; // Prevent page change during recalculation
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isRecalculating) return; // Prevent rows per page change during recalculation
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const filteredData = reportData.filter(row =>
    row.pharmacyNum.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatValue = (value: any, field: string): React.ReactNode => {
    if (field === 'audit') {
      return value === 1 ? 'Да' : 'Нет';
    }

    if (field === 'checkStatus') {
      return value === 1 ? (
        <CheckCircle color="success" sx={{ fontSize: 20 }} />
      ) : (
        <Cancel color="error" sx={{ fontSize: 20 }} />
      );
    }

    if (typeof value === 'number' || !isNaN(Number(value))) {
      return Number(value).toLocaleString('ru-RU', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
      });
    }
    return value;
  };

  const isRowHighlighted = (row: ReportData): boolean => {
    return row.audit === 1 &&
      ((row.overage === 0 || row.overage === null) &&
        (row.shortfall === 0 || row.shortfall === null));
  };

  const createNewRecords = async () => {
    if (isRecalculating) return; // Prevent creating records during recalculation
    
    try {
      const [year, month] = selectedMonth.split('-');

      const response = await axios.post('https://tbl.zerdegroup.kz/api/mathdata-create', {
        month: Number(month),
        year: Number(year)
      });

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: `Записи успешно созданы. ${response.data.message}`,
          severity: 'success',
        });
        fetchReportData();
      }
    } catch (error) {
      console.error('Ошибка при создании записей:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при создании записей: ' + error,
        severity: 'error',
      });
    }
  };

  const handleRecalculate = async () => {
    setIsRecalculating(true);
    setEditingCell(null); // Cancel any active cell editing
    
    try {
      const [year, month] = selectedMonth.split('-');

      const response = await axios.post('https://tbl.zerdegroup.kz/api/mathdata-recalculate', {
        month: Number(month),
        year: Number(year)
      });

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: `Пересчет выполнен успешно. Обновлено месяцев: ${response.data.updatedMonths}`,
          severity: 'success',
        });
        fetchReportData();
      }
    } catch (error) {
      console.error('Ошибка при пересчете:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при пересчете: ' + error,
        severity: 'error',
      });
    } finally {
      setIsRecalculating(false);
    }
  };

  const handleReindex = async () => {
    if (isRecalculating) return; // Prevent reindexing during recalculation
    
    try {
      const [year, month] = selectedMonth.split('-');

      const response = await axios.post('https://tbl.zerdegroup.kz/api/mathdata-reindex', {
        month: Number(month),
        year: Number(year)
      });

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: `Переиндексация выполнена успешно. ${response.data.message}`,
          severity: 'success',
        });
        fetchReportData();
      }
    } catch (error) {
      console.error('Ошибка при переиндексации:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при переиндексации: ' + error,
        severity: 'error',
      });
    }
  };

  const handleGetAllReport = async (rowId: number) => {
    if (isRecalculating) return; // Prevent report generation during recalculation
    
    try {
      const row = reportData.find(r => r.id === rowId);
      if (!row) {
        throw new Error('Данные не найдены');
      }
  
      const userLogin = row.pharmacyNum; 
  
      const userResponse = await axios.get('https://tbl.zerdegroup.kz/api/users-data-mathrep', {
        params: {
          login: userLogin
        }
      });
  
      if (!userResponse.data) {
        throw new Error('Пользователь не найден');
      }
  
      const { pharmacyNum, fullName } = userResponse.data;
      
      await generateMathReport(
        row,
        selectedMonth,
        pharmacyNum, 
        fullName 
      );
  
      setSnackbar({
        open: true,
        message: 'Отчет успешно сформирован',
        severity: 'success',
      });
    } catch (error) {
      console.error('Ошибка при получении отчета:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при формировании отчета: ' + error,
        severity: 'error',
      });
    }
  };

  // Fixed the type to match Material-UI's SelectChangeEvent
  const handleMonthChange = (event: SelectChangeEvent<string>) => {
    if (isRecalculating) return;
    setSelectedMonth(event.target.value);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isRecalculating) return; // Prevent search changes during recalculation
    setSearchQuery(e.target.value);
  };

  return (
    <Box sx={{ maxWidth: '100%', overflowX: 'auto', position: 'relative' }}>
      {/* Backdrop overlay that blocks UI while recalculating */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 9999,
          position: 'fixed',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
        open={isRecalculating}
      >
        <CircularProgress color="inherit" />
        <Box sx={{ typography: 'h6' }}>
          Выполняется пересчет, пожалуйста подождите...
        </Box>
      </Backdrop>

      <Box sx={{ display: 'flex', alignItems: 'stretch', justifyContent: 'space-between', mb: 2, gap: 2, mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
          <Select
            size='small'
            value={selectedMonth}
            onChange={handleMonthChange}
            sx={{ minWidth: 200 }}
            disabled={isRecalculating}
          >
            {getMonthsList().map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </Select>

          <TextField
            size="small"
            label="Поиск по номеру аптеки"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ minWidth: 200 }}
            disabled={isRecalculating}
          />
        </Box>

        <Box>
          {localStorage.getItem('role') === 'admin' && (
            <Button
              variant="contained"
              color="warning"
              onClick={handleReindex}
              sx={{ minWidth: 100, mr: 2 }}
              disabled={isRecalculating}
            >
              Перепроверка
            </Button>
          )}
          <Button
            variant="contained"
            color="error"
            onClick={handleRecalculate}
            startIcon={isRecalculating ? <CircularProgress size={20} /> : null}
            sx={{ minWidth: 150 }}
            disabled={isRecalculating}
          >
            {isRecalculating ? 'Выполняется' : 'Пересчет'}
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      ) : reportData.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            variant="contained"
            onClick={createNewRecords}
            startIcon={<Add />}
            disabled={isRecalculating}
          >
            Создать записи
          </Button>
        </Box>
      ) : (
        <Paper sx={{ mt: 2 }}>
          <TableContainer sx={{ borderRadius: 1, maxHeight: 600 }}>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((column, index) => (
                    <TableCell
                      key={column.field}
                      sx={{
                        whiteSpace: 'nowrap',
                        backgroundColor: '#1976d2',
                        position: 'sticky',
                        top: 0,
                        zIndex: index === 0 ? 2 : 1,
                        color: 'white',
                        ...(index === 0 && {
                          left: 0,
                          backgroundColor: '#186fc5'
                        })
                      }}>
                      {column.headerName}
                    </TableCell>
                  ))}
                  <TableCell
                    sx={{
                      whiteSpace: 'nowrap',
                      backgroundColor: '#1976d2',
                      position: 'sticky',
                      top: 0,
                      right: 0,
                      zIndex: 2,
                      color: 'white',
                    }}
                  >
                    МатОтчет
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{
                        backgroundColor: isRowHighlighted(row) ? '#ffebee' : 'inherit',
                        pointerEvents: isRecalculating ? 'none' : 'auto'
                      }}
                    >
                      {columns.map((column, index) => (
                        <TableCell
                          key={`${row.id}-${column.field}`}
                          onClick={() => handleCellClick(row.id, column.field, row[column.field])}
                          sx={{
                            ...(column.editable && { cursor: isRecalculating ? 'not-allowed' : 'pointer' }),
                            ...(index === 0 && {
                              position: 'sticky',
                              left: 0,
                              backgroundColor: isRowHighlighted(row) ? '#ffebee' : 'rgb(245, 245, 245)',
                              zIndex: 1
                            }),
                            ...(column.field === 'checkStatus' && {
                              textAlign: 'center'
                            }),
                            ...(isRecalculating && { opacity: 0.7 })
                          }}
                        >
                          {editingCell?.rowId === row.id && editingCell?.field === column.field ? (
                            <TextField
                              value={editingCell.value.toString()}
                              onChange={handleCellChange}
                              onBlur={handleCellBlur}
                              variant="standard"
                              autoFocus
                              size="small"
                              disabled={isRecalculating}
                            />
                          ) : (
                            formatValue(row[column.field] ?? '', column.field)
                          )}
                        </TableCell>
                      ))}
                      <TableCell
                        sx={{
                          position: 'sticky',
                          right: 0,
                          backgroundColor: isRowHighlighted(row) ? '#ffebee' : 'white',
                          zIndex: 1,
                          padding: '4px 8px',
                          textAlign: 'center'
                        }}
                      >
                        <Button
                          size="small"
                          onClick={() => handleGetAllReport(row.id)}
                          variant='contained'
                          color='primary'
                          disabled={isRecalculating}
                        >
                          <Download />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            disabled={isRecalculating}
          />
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CurrentMathReport;